import React, { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import { axiosSalary } from "../api/axiosInstance";
import { useAuth } from "../auth/AuthContext";
import BulkManagerSessionEntry from "./BulkManagerSessionEntry"; // ✅ ADD

const ManagerAttendanceControl = () => {

  const { user } = useAuth();
  const groupId = user?.groupId;

  const [restaurantList, setRestaurantList] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  const [attendanceDate, setAttendanceDate] = useState("");
  const [data, setData] = useState([]);
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const [filterType, setFilterType] = useState("ALL"); // ✅ NEW
  const [bulkOpen, setBulkOpen] = useState(false); // ✅ NEW

  /* ================= DEFAULT DATE ================= */
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setAttendanceDate(today);
  }, []);

  /* ================= FETCH RESTAURANTS ================= */
  const fetchRestaurants = async () => {
    try {
      const res = await axiosSalary.get(
        `/RestaurantMaster/GetByGroup?groupId=${groupId}`
      );

      const options = (res.data || []).map(r => ({
        value: r.restId,
        label: r.restaurantName
      }));

      setRestaurantList(options);
      setSelectedRestaurant(options[0]);

    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (groupId) fetchRestaurants();
  }, [groupId]);

  /* ================= FETCH SHEET ================= */
  const fetchSheet = async () => {
    if (!selectedRestaurant?.value || !attendanceDate) return;

    try {
      setLoading(true);

      const res = await axiosSalary.get(
        `/ManagerAttendanceEntry/GetAttendanceEntrySheet`,
        {
          params: {
            groupId,
            restId: selectedRestaurant.value,
            attendanceDate
          }
        }
      );

      setRestaurantInfo(res.data);
      setData(res.data?.employees || []);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSheet();
  }, [selectedRestaurant, attendanceDate]);

  /* ================= FILTER ================= */
  const filteredData = useMemo(() => {
    if (filterType === "PRESENT") {
      return data.filter(e => e.attendanceExists);
    }
    if (filterType === "ABSENT") {
      return data.filter(e => !e.attendanceExists);
    }
    return data;
  }, [data, filterType]);

  /* ================= STATUS ================= */
  const getStatus = (emp) => {
    if (!emp.attendanceExists) {
      return <span className="bg-gray-300 px-2 py-1 rounded text-xs">Absent</span>;
    }
    if (emp.isFinalized) {
      return <span className="bg-green-200 text-green-700 px-2 py-1 rounded text-xs">Finalized</span>;
    }
    return <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">Present</span>;
  };

  /* ================= SUMMARY ================= */
  const summary = useMemo(() => {
    let present = 0, absent = 0;

    data.forEach(emp => {
      if (emp.attendanceExists) present++;
      else absent++;
    });

    return {
      total: data.length,
      present,
      absent
    };
  }, [data]);

  return (
    <div className="bg-white p-4 md:p-5 rounded-xl shadow border">
        {/* ✅ INFO NOTE */}
<div className="text-xs md:text-sm mb-2">

  {filterType === "ALL" && (
    <div className="bg-blue-50 text-blue-700 px-3 py-2 rounded">
      Click <b>Absent</b> to add bulk sessions for missing attendance.
    </div>
  )}

  {filterType === "PRESENT" && (
    <div className="bg-green-50 text-green-700 px-3 py-2 rounded">
      Showing only present employees.
    </div>
  )}

  {filterType === "ABSENT" && (
    <div className="bg-orange-50 text-orange-700 px-3 py-2 rounded">
      You are viewing absent employees. Click <b>Bulk Add Session</b> to mark attendance.
    </div>
  )}

</div>

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between gap-3 mb-3">

        <h2 className="text-lg font-semibold">
          Manager Attendance Control
        </h2>

        <div className="flex flex-wrap gap-2 items-center ml-auto">

          <Select
            value={selectedRestaurant}
            onChange={setSelectedRestaurant}
            options={restaurantList}
            className="text-sm min-w-[180px]"
          />

          <input
            type="date"
            value={attendanceDate}
            onChange={(e) => setAttendanceDate(e.target.value)}
            className="border px-2 py-1 rounded text-sm"
          />

        </div>
      </div>

      {/* SUMMARY (CLICKABLE) */}
      <div className="grid grid-cols-3 gap-2 text-xs md:text-sm mb-3">

        <div
          onClick={() => setFilterType("ALL")}
          className="bg-gray-100 p-2 rounded text-center cursor-pointer hover:bg-gray-200"
        >
          <p>Total</p>
          <p>{summary.total}</p>
        </div>

        <div
          onClick={() => setFilterType("PRESENT")}
          className="bg-green-100 p-2 rounded text-center cursor-pointer hover:bg-green-200"
        >
          <p>Present</p>
          <p>{summary.present}</p>
        </div>

        <div
          onClick={() => setFilterType("ABSENT")}
          className="bg-gray-300 p-2 rounded text-center cursor-pointer hover:bg-gray-400"
        >
          <p>Absent</p>
          <p>{summary.absent}</p>
        </div>

      </div>

      {/* ✅ BULK BUTTON (ONLY ABSENT) */}
      {filterType === "ABSENT" && filteredData.length > 0 && (
        <button
          onClick={() => setBulkOpen(true)}
          className="bg-blue-600 text-white px-3 py-1 rounded text-sm mb-3"
        >
          Bulk Add Session
        </button>
      )}

      {/* RESTAURANT INFO */}
      {restaurantInfo && (
        <div className="text-xs mb-3 bg-gray-100 p-2 rounded">
          <b>{restaurantInfo.restaurantName}</b> | Open: {restaurantInfo.openingTime} - {restaurantInfo.closingTime}
        </div>
      )}

      {/* LOADING */}
      {loading && (
        <div className="text-center py-3 text-blue-600 text-sm">
          Loading...
        </div>
      )}

      {/* DESKTOP */}
      <div className="hidden md:block border rounded-lg max-h-[500px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-200 sticky top-0">
            <tr>
              <th className="border p-2">Emp ID</th>
              <th className="border p-2">Name</th>
              <th className="border p-2">Shift</th>
              <th className="border p-2">Status</th>
            </tr>
          </thead>

          <tbody>
            {filteredData.map(emp => (
              <tr key={emp.empId}>
                <td className="border p-2">{emp.empId}</td>
                <td className="border p-2">{emp.empName}</td>
                <td className="border p-2 text-center">{emp.shiftId || "-"}</td>
                <td className="border p-2">{getStatus(emp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MOBILE */}
      <div className="md:hidden space-y-3 mt-3">
        {filteredData.map(emp => (
          <div key={emp.empId} className="border p-3 rounded-xl shadow-sm">

            <div className="flex justify-between">
              <h3 className="font-semibold text-sm">{emp.empName}</h3>
              {getStatus(emp)}
            </div>

            <div className="text-xs text-gray-600">
              ID: {emp.empId}
            </div>

            <div className="mt-1 text-xs">
              Shift: {emp.shiftId || "-"}
            </div>

          </div>
        ))}
      </div>

      {/* ✅ BULK MODAL */}
      <BulkManagerSessionEntry
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        employees={filteredData} // only absent
        restId={selectedRestaurant?.value}
        attendanceDate={attendanceDate}
        onSuccess={fetchSheet}
      />

    </div>
  );
};

export default ManagerAttendanceControl;